import { z } from 'zod';
import type { BlockDefinition } from '../types';
import { sanitizedHtmlProps } from '../lib/sanitize';

export const mediaMarqueeSchema = z.object({
  label: z.string().max(200).optional(),
  items: z
    .array(
      z.object({
        name: z.string().min(1).max(80),
        wordmark: z.string().min(1).max(120),
        style: z.string().max(40).optional(),
      }),
    )
    .min(3)
    .max(20),
});

export type MediaMarqueeData = z.infer<typeof mediaMarqueeSchema>;

export function MediaMarqueeRenderer({ label, items }: MediaMarqueeData) {
  const doubled = [...items, ...items];
  return (
    <section className="v3-marquee-section">
      {label ? (
        <div className="container">
          <div className="v3-marquee-label">{label}</div>
        </div>
      ) : null}
      <div className="v3-marquee">
        <div className="v3-marquee-track">
          {doubled.map((item, i) => (
            <div
              key={i}
              className={`v5-wm${item.style ? ` v5-wm-${item.style}` : ''}`}
              dangerouslySetInnerHTML={sanitizedHtmlProps(item.wordmark)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export const mediaMarqueeBlock: BlockDefinition<MediaMarqueeData> = {
  meta: {
    type: 'media-marquee',
    label: 'Marquee de medios (v5)',
    description: 'Carrusel infinito con wordmarks de medios (El País, Forbes, etc). Logos texto, no imágenes.',
    category: 'social-proof',
    icon: 'newspaper',
  },
  schema: mediaMarqueeSchema,
  defaults: {
    label: 'Han hablado de nosotros · Apariciones en medios económicos y tecnológicos',
    items: [
      { name: 'El País', wordmark: '<span class="wm-bold">EL&nbsp;PAÍS</span>', style: 'elpais' },
      { name: 'Expansión', wordmark: 'EXPANSIÓN', style: 'expansion' },
      { name: 'Cinco Días', wordmark: 'CINCO<span class="wm-thin"> DÍAS</span>', style: 'cincodias' },
      { name: 'Forbes', wordmark: '<span class="wm-italic">Forbes</span>', style: 'forbes' },
      { name: 'TechCrunch', wordmark: 'Tech<span class="wm-accent">Crunch</span>', style: 'techcrunch' },
      { name: 'El Mundo', wordmark: 'EL&nbsp;MUNDO', style: 'elmundo' },
      { name: 'El Confidencial', wordmark: '<span class="wm-red">El</span>Confidencial', style: 'elconfidencial' },
      { name: 'La Vanguardia', wordmark: '<em>La&nbsp;Vanguardia</em>', style: 'lavanguardia' },
      { name: 'Emprendedores', wordmark: 'Emprendedores', style: 'emprendedores' },
      { name: 'El Economista', wordmark: '<span class="wm-italic">el</span>Economista', style: 'eleconomista' },
      { name: 'Bloomberg', wordmark: 'BLOOMBERG', style: 'bloomberg' },
      { name: 'Business Insider', wordmark: 'Business&nbsp;Insider', style: 'business' },
    ],
  },
  fields: [
    { name: 'label', label: 'Etiqueta superior', type: 'text', max: 200 },
    {
      name: 'items',
      label: 'Medios',
      type: 'array',
      max: 20,
      itemFields: [
        { name: 'name', label: 'Nombre (interno)', type: 'text', required: true, max: 80 },
        { name: 'wordmark', label: 'Wordmark (HTML simple permitido)', type: 'text', required: true, max: 120 },
        { name: 'style', label: 'Clase CSS sufijo (ej. elpais)', type: 'text', max: 40 },
      ],
    },
  ],
  Renderer: MediaMarqueeRenderer,
};
